// Built-in strings for the webclient Voice feature, per language. The fields
// listed in VOICE_TEXT_FIELDS can be overridden per bot language from the
// webclient config (`options.voice.texts`); everything else is always shown
// from these built-in sets. resolveVoiceMessages in
// ../helpers/voice-messages.helper.ts merges the two.

// * Kept free of imports: the webclient config types import this file, so an
// * import back from them would form a cycle.

// Languages the Voice tab UI (labels, statuses, errors) can be shown in.
// The order here is the order of the language dropdown in the admin portal.
export const VOICE_UI_LANGUAGES = ['nl', 'en'] as const;

export type VoiceUILanguage = (typeof VOICE_UI_LANGUAGES)[number];

export const DEFAULT_VOICE_UI_LANGUAGE: VoiceUILanguage = 'nl';

// Short `message` (bold, red) plus a `hint` (smaller, grey) explaining how to
// resolve the issue.
type VoiceErrorMessages = {
  message: string;
  hint: string;
};

export type VoiceMessages = {
  status: {
    idle: string;
    // Second, smaller line under the idle text. Empty hides it.
    idleSubtitle: string;
    connecting: string;
    speaking: string;
    listening: string;
    connectionFailed: string;
  };
  buttons: {
    startCall: string;
    tryAgain: string;
    connecting: string;
    endCall: string;
    // Tooltip on the disabled start button. Empty shows no tooltip.
    offlineTitle: string;
  };
  // Avatar image alt text
  avatarAlt: string;
  // Shown in a voice-only widget that has no usable agent configured.
  unavailable: string;
  errors: {
    permissionDenied: VoiceErrorMessages;
    permissionBlocked: VoiceErrorMessages;
    noDevice: VoiceErrorMessages;
    deviceInUse: VoiceErrorMessages;
    connection: VoiceErrorMessages;
    generic: VoiceErrorMessages;
  };
  tabs: {
    chat: string;
    voice: string;
    // Badge next to the voice tab label, shown only when the badge is enabled.
    newBadge: string;
  };
  // Shown above the tab bar when the user tries to leave the Voice tab while a
  // call is in progress.
  tabSwitchConfirm: {
    question: string;
    cancel: string;
    confirm: string;
  };
  // * Not shown to the user - this is prompt text sent to the ElevenLabs agent
  // * as a contextual update, telling it to wrap up and end the call itself.
  wrapUpNotice: string;
};

const ENGLISH_VOICE_MESSAGES: VoiceMessages = {
  status: {
    idle: 'Start a voice conversation',
    idleSubtitle:
      'Rather talk than type? Ask your question out loud and get an answer right away.',
    connecting: 'Connecting…',
    speaking: 'Speaking…',
    listening: 'Listening to you…',
    connectionFailed: 'Connection failed. Please try again.',
  },

  buttons: {
    startCall: 'Start call',
    tryAgain: 'Try again',
    connecting: 'Connecting…',
    endCall: 'End call',
    offlineTitle: 'No internet connection',
  },

  avatarAlt: 'Voice assistant',

  unavailable: 'Voice is currently unavailable.',

  errors: {
    permissionDenied: {
      message: 'Microphone access was denied.',
      hint: 'To use voice, allow microphone access in your browser settings and try again.',
    },
    permissionBlocked: {
      message: 'Microphone access is blocked.',
      hint: 'To use voice, allow microphone access in your browser settings and reload the page.',
    },
    noDevice: {
      message: 'No microphone found.',
      hint: 'Please connect a microphone and try again.',
    },
    deviceInUse: {
      message: 'Microphone is already in use.',
      hint: 'Close other apps that may be using the microphone and try again.',
    },
    connection: {
      message: 'Voice calls are blocked by your network.',
      hint: 'Your network or firewall may be blocking the voice connection. Please contact your IT administrator.',
    },
    generic: {
      message: 'Something went wrong.',
      hint: 'Please try again.',
    },
  },

  tabs: {
    chat: 'Chat',
    voice: 'Voice',
    newBadge: 'NEW',
  },

  tabSwitchConfirm: {
    question: 'End call and switch to Chat?',
    cancel: 'Cancel',
    confirm: 'End & Switch',
  },

  wrapUpNotice:
    'The call is approaching its maximum length and will end shortly. Bring the conversation to a natural close now: summarise briefly, offer any final help, say goodbye, and end the call.',
};

const DUTCH_VOICE_MESSAGES: VoiceMessages = {
  status: {
    idle: 'Start een belgesprek',
    idleSubtitle:
      'Liever praten dan typen? Stel je vraag hardop en krijg direct antwoord.',
    connecting: 'Verbinden…',
    speaking: 'De assistent spreekt…',
    listening: 'Luistert naar je…',
    connectionFailed: 'Verbinding mislukt. Probeer het opnieuw.',
  },

  buttons: {
    startCall: 'Start gesprek',
    tryAgain: 'Opnieuw proberen',
    connecting: 'Verbinden…',
    endCall: 'Gesprek beëindigen',
    offlineTitle: 'Geen internetverbinding',
  },

  avatarAlt: 'Voice Assistent',

  unavailable: 'Spraak is momenteel niet beschikbaar.',

  errors: {
    permissionDenied: {
      message: 'Toegang tot de microfoon is geweigerd.',
      hint: 'Sta microfoontoegang toe in je browserinstellingen en probeer het opnieuw.',
    },
    permissionBlocked: {
      message: 'Toegang tot de microfoon is geblokkeerd.',
      hint: 'Sta microfoontoegang toe in je browserinstellingen en herlaad de pagina.',
    },
    noDevice: {
      message: 'Geen microfoon gevonden.',
      hint: 'Sluit een microfoon aan en probeer het opnieuw.',
    },
    deviceInUse: {
      message: 'De microfoon is al in gebruik.',
      hint: 'Sluit andere apps die de microfoon gebruiken en probeer het opnieuw.',
    },
    connection: {
      message: 'Spraakgesprekken worden geblokkeerd door je netwerk.',
      hint: 'Je netwerk of firewall blokkeert mogelijk de spraakverbinding. Neem contact op met je IT-beheerder.',
    },
    generic: {
      message: 'Er is iets misgegaan.',
      hint: 'Probeer het opnieuw.',
    },
  },

  tabs: {
    chat: 'Chat',
    voice: 'Call',
    newBadge: 'NIEUW',
  },

  tabSwitchConfirm: {
    question: 'Gesprek beëindigen en naar Chat gaan?',
    cancel: 'Cancel',
    confirm: 'Beëindigen & wisselen',
  },

  wrapUpNotice:
    'Het gesprek nadert de maximale duur en wordt binnenkort beëindigd. Rond het gesprek nu op een natuurlijke manier af: vat kort samen, bied nog een laatste keer hulp aan, neem afscheid en beëindig het gesprek.',
};

const VOICE_MESSAGES_BY_LANGUAGE: Record<VoiceUILanguage, VoiceMessages> = {
  en: ENGLISH_VOICE_MESSAGES,
  nl: DUTCH_VOICE_MESSAGES,
};

// Falls back to the default language for configs saved before the language
// option existed, and for any unknown value coming from an older config.
export const getVoiceMessages = (language?: string): VoiceMessages => {
  const messages = VOICE_MESSAGES_BY_LANGUAGE[language as VoiceUILanguage];
  if (messages) {
    return messages;
  }
  return VOICE_MESSAGES_BY_LANGUAGE[DEFAULT_VOICE_UI_LANGUAGE];
};

export type VoiceTextGroup = 'status' | 'buttons' | 'tabs';

export type VoiceTextField = {
  group: VoiceTextGroup;
  key: string;
  // A required text is the only content of its control, so it cannot be saved
  // empty. An optional one is hidden when left empty.
  isRequired: boolean;
  // The tab bar only exists in a chat studio that also offers voice.
  isChatStudioOnly: boolean;
};

// The built-in texts an admin can override from the webclient config, in form
// order. Everything else in VoiceMessages stays built-in.
export const VOICE_TEXT_FIELDS: readonly VoiceTextField[] = [
  {
    group: 'status',
    key: 'idle',
    isRequired: true,
    isChatStudioOnly: false,
  },
  {
    group: 'status',
    key: 'idleSubtitle',
    isRequired: false,
    isChatStudioOnly: false,
  },
  {
    group: 'status',
    key: 'connecting',
    isRequired: true,
    isChatStudioOnly: false,
  },
  {
    group: 'status',
    key: 'speaking',
    isRequired: true,
    isChatStudioOnly: false,
  },
  {
    group: 'status',
    key: 'listening',
    isRequired: true,
    isChatStudioOnly: false,
  },
  {
    group: 'status',
    key: 'connectionFailed',
    isRequired: true,
    isChatStudioOnly: false,
  },
  {
    group: 'buttons',
    key: 'startCall',
    isRequired: true,
    isChatStudioOnly: false,
  },
  {
    group: 'buttons',
    key: 'tryAgain',
    isRequired: true,
    isChatStudioOnly: false,
  },
  {
    group: 'buttons',
    key: 'connecting',
    isRequired: true,
    isChatStudioOnly: false,
  },
  {
    group: 'buttons',
    key: 'endCall',
    isRequired: true,
    isChatStudioOnly: false,
  },
  {
    group: 'buttons',
    key: 'offlineTitle',
    isRequired: false,
    isChatStudioOnly: false,
  },
  {
    group: 'tabs',
    key: 'chat',
    isRequired: true,
    isChatStudioOnly: true,
  },
  {
    group: 'tabs',
    key: 'voice',
    isRequired: true,
    isChatStudioOnly: true,
  },
  {
    group: 'tabs',
    key: 'newBadge',
    isRequired: true,
    isChatStudioOnly: true,
  },
];

export const VOICE_NEW_BADGE_DEFAULT_COLORS = {
  backgroundColor: '#00B140',
  textColor: '#FFFFFF',
};
